import {
  addToast,
  Button,
  CheckInput,
  DateInput,
  Modal,
  Select,
  SelectOptionType,
  TextAreaInput,
  TextInput,
} from '@octano/global-ui';
import {
  forwardRef,
  Ref,
  useCallback,
  useImperativeHandle,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Container, Row } from 'reactstrap';
import { useActionKitImp } from '../../../../components/ozmoit/common/action-kit-imp';
import { TranslationsKeys } from '../../../../locales/translations';
import { createWarningRecord } from '../../actions';
import { useWarningsSummaryDetailsFormLoaderData } from '../../loaders/StudentWarningsSummaryDetailsFormLoader';
import { useWarningsSummaryDetailsLoader } from '../../loaders/StudentWarningsSummaryDetailsLoader';
import { StudentWarningsGroupType } from '../../types';

export type BehaviorWarningGroupRecord = {
  id: number;
  title: string;
  date: string;
  notes: string | null;
  type: string;
  severity: string;
  requiresDetention: boolean;
};

export type BehaviorWarningGroupModalMethods = {
  open: (record?: BehaviorWarningGroupRecord) => void;
  close: () => void;
};

type BehaviorWarningGroupModalProps = {
  onSave?: () => void;
};

type FormFields = {
  title: string;
  date: string;
  notes?: string | null;
  type: SelectOptionType | null;
  severity: SelectOptionType | null;
  requiresDetention: boolean;
};

const DEFAULT_VALUES: FormFields = {
  title: '',
  date: '',
  notes: null,
  type: null,
  severity: null,
  requiresDetention: false,
};

const BehaviorWarningGroupModal = (
  { onSave }: BehaviorWarningGroupModalProps,
  ref: Ref<BehaviorWarningGroupModalMethods>,
) => {
  const data = useWarningsSummaryDetailsFormLoaderData();
  const { query } = useWarningsSummaryDetailsLoader();
  const { t } = useTranslation(TranslationsKeys.STUDENT_ALERTS_DETAILS);
  const [visible, setVisible] = useState(false);
  const [record, setRecord] = useState<BehaviorWarningGroupRecord>();

  const { control, handleSubmit, reset } = useForm<FormFields>({
    defaultValues: DEFAULT_VALUES,
  });

  const { action: handleClickSave, loading } = useActionKitImp(
    createWarningRecord,
    {
      onSuccess: () => {
        addToast({
          color: 'success',
          text: t(`group.behavior.modal.success`),
        });
        !!onSave && onSave();
        setVisible(false);
        setRecord(undefined);
      },
      onError: {
        unknown: () => {
          addToast({
            color: 'danger',
            text: t(`group.behavior.modal.failed`),
          });
        },
      },
    },
  );

  const handleOpen = useCallback(
    (nextRecord?: BehaviorWarningGroupRecord) => {
      if (nextRecord) {
        reset({
          title: nextRecord.title,
          date: nextRecord.date,
          notes: nextRecord.notes,
          type:
            data?.behaviorRecordTypes.find(
              (option) => option.value === nextRecord.type,
            ) || null,
          severity:
            data?.behaviorRecordSeverities.find(
              (option) => option.value === nextRecord.severity,
            ) || null,
          requiresDetention: nextRecord.requiresDetention,
        });
      } else {
        reset(DEFAULT_VALUES);
      }
      setVisible(true);
      setRecord(nextRecord);
    },
    [data, reset],
  );

  const handleClose = useCallback(() => {
    setVisible(false);
    setRecord(undefined);
  }, []);

  useImperativeHandle(ref, () => ({
    open: handleOpen,
    close: handleClose,
  }));

  const handleSave = useCallback(
    async (data: FormFields) => {
      if (loading) {
        return;
      }
      handleClickSave(StudentWarningsGroupType.Behavior, {
        ...data,
        studyPlanEnrollmentId: query.studyPlanEnrollmentId,
        type: data?.type?.value,
        severity: data?.severity?.value,
      });
    },
    [loading, query, handleClickSave],
  );

  return (
    <Modal isOpen={!!visible} size="lg" toggle={handleClose}>
      <Container fluid>
        <Row>
          <Col xs={12}>
            <h2 className="text-dark fs-22 mb-0 w-100 text-center">
              {record?.id
                ? t(`group.behavior.modal.titleEdit`)
                : t(`group.behavior.modal.title`)}
            </h2>
            <p className="fs-16 w-100 text-center">
              {record?.id
                ? t(`group.behavior.modal.descriptionEdit`)
                : t(`group.behavior.modal.description`)}
            </p>
          </Col>
        </Row>
        <Row className="my-5">
          <Col xs={12} md={4}>
            <Select
              control={control}
              name="type"
              options={data?.behaviorRecordTypes || []}
              label={t(`group.behavior.table.type`)}
              isClearable={false}
              rules={{ required: true }}
              disabled={!!record?.id}
            />
          </Col>
          <Col xs={12} md={4}>
            <Select
              control={control}
              name="severity"
              options={data?.behaviorRecordSeverities || []}
              label={t(`group.behavior.table.severity`)}
              isClearable={false}
              rules={{ required: true }}
              disabled={!!record?.id}
            />
          </Col>
          <Col xs={12} md={4}>
            <DateInput
              control={control}
              name="date"
              label={t(`group.behavior.table.date`)}
              rules={{ required: true }}
              maxDate={new Date()}
              disabled={!!record?.id}
            />
          </Col>
          <Col xs={12} md={12}>
            <TextInput
              control={control}
              name="title"
              label={t(`group.behavior.table.title`)}
              rules={{ required: true }}
              disabled={!!record?.id}
            />
          </Col>
          <Col className="mb-5" xs={12} md={12}>
            <TextAreaInput
              control={control}
              name="notes"
              label={t(`group.behavior.table.notes`)}
              rules={{ required: false }}
              disabled={!!record?.id}
            />
          </Col>
          <Col xs={12} md={12}>
            <CheckInput
              control={control}
              name="requiresDetention"
              label={t(`group.behavior.table.requiresDetention`)}
              disabled={!!record?.id}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={record?.id ? 12 : 6}>
            <Button
              text={
                record?.id
                  ? t('common:actions.close')
                  : t('common:actions.cancel')
              }
              type="button"
              fullwidth
              outlined
              onClick={handleClose}
            />
          </Col>
          {!record?.id && (
            <Col xs={12} md={6}>
              <Button
                text={t('common:actions.add')}
                type="button"
                fullwidth
                onClick={handleSubmit(handleSave)}
                loading={loading}
              />
            </Col>
          )}
        </Row>
      </Container>
    </Modal>
  );
};

export default forwardRef(BehaviorWarningGroupModal);
